import type { Family, FamilyChild, FamilyGroup, FamilyInviteState } from "@teamna/shared";
import type { ReactNode } from "react";
import { Avatar, ButtonLink } from "../../components/index.ts";
import { WhatsAppIcon } from "../../components/PhoneSignIn.tsx";
import { childGroupText, columnOf, phoneText, whatsappHref, type Words } from "./model.ts";

/**
 * Small pieces the Families views and the Home share: the WhatsApp button, a child's group tag,
 * the invite badge.
 */

/** Opens a chat in the owner's own WhatsApp, with the first words ready. */
export function WhatsAppButton({
  phoneE164,
  text,
  label,
  children,
  fullWidth = false,
}: {
  phoneE164: string;
  text?: string;
  /** The accessible name when the visible words are short ("WhatsApp"). */
  label?: string;
  children: ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <ButtonLink
      variant="whatsapp"
      href={whatsappHref(phoneE164, text)}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      fullWidth={fullWidth}
      className="gap-2"
    >
      <WhatsAppIcon className="size-4" />
      {children}
    </ButtonLink>
  );
}

/** A group's colour as a dot, or a hollow dot for "no group yet". */
export function GroupDot({ color, size = 10 }: { color: string | null; size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block shrink-0 rounded-full"
      style={
        color
          ? { width: size, height: size, background: color }
          : { width: size, height: size, border: "2px solid var(--color-late-line)", background: "transparent" }
      }
    />
  );
}

/**
 * "Omar, 7: Level 1". Gold when the child has no group yet, dashed when the name is still missing.
 * A child with a profile photo shows it at the start of the tag.
 */
export function ChildTag({ child, groups, words }: { child: FamilyChild; groups: FamilyGroup[]; words: Words }) {
  const column = columnOf(child, groups);
  const group = groups.find((row) => row.id === column) ?? null;
  const name = child.nameMissing ? `${child.name} (name not set)` : `${child.name}${child.age !== null ? `, ${child.age}` : ""}`;
  return (
    <span
      className={[
        "inline-flex min-h-7 items-center gap-1.5 rounded-pill pr-2.5 text-[13px] font-semibold",
        child.photoUrl ? "pl-1" : "pl-2.5",
        group ? "bg-sunken text-ink" : "bg-late-surface text-late-ink",
        child.nameMissing ? "border border-dashed border-late-line" : "",
      ].join(" ")}
    >
      {child.photoUrl ? <Avatar name={child.name} photoUrl={child.photoUrl} size={20} /> : null}
      <GroupDot color={group?.color ?? null} size={8} />
      <span>
        <span dir="auto">{name}</span>
        <span className="font-normal opacity-80">: {childGroupText(child, groups, words)}</span>
      </span>
    </span>
  );
}

const INVITE_TEXT: Record<FamilyInviteState, string> = { none: "Not invited", invited: "Invited", joined: "Joined" };

export function InviteBadge({ state }: { state: FamilyInviteState }) {
  return (
    <span
      className={[
        "inline-flex min-w-[92px] justify-center rounded-pill px-2.5 py-1 text-[12.5px] font-bold whitespace-nowrap",
        state === "joined" ? "bg-confirmed-surface text-confirmed-ink" : state === "invited" ? "bg-accent-surface text-accent-strong" : "bg-sunken text-ink-2",
      ].join(" ")}
    >
      {INVITE_TEXT[state]}
    </span>
  );
}

/** The name cell of a member row: the face, the name, the numbers and the hold. The Clients table uses it too. */
export function FamilyNameCell({ family, name }: { family: Family; name: string }) {
  return (
    <span className="flex min-w-[200px] items-center gap-3">
      <Avatar name={name} photoUrl={family.adults[0]?.photoUrl ?? null} size={36} />
      <span className="min-w-0">
        <span className="block font-semibold text-ink" dir="auto">
          {name}
        </span>
        <span className="block text-[13px] text-ink-2">
          {family.adults.some((adult) => adult.phoneE164)
            ? family.adults
                .filter((adult) => adult.phoneE164)
                .map((adult) => phoneText(adult.phoneE164))
                .join(", ")
            : "No number"}
        </span>
        {family.holdNote ? (
          <span className="mt-1 inline-block rounded-pill bg-late-surface px-2.5 py-0.5 text-[12.5px] font-semibold text-late-ink" dir="auto">
            On hold: {family.holdNote}
          </span>
        ) : null}
      </span>
    </span>
  );
}
